// Picker de poses para el modo "Imagen con modelo" de Interfaz V2.
//
// Multi-select de poses del catálogo (`poses` table). Dos variantes:
//
//   • ModelPickerInline (desktop) — un carrusel horizontal compacto con
//     las poses + un botón "Ver más" que abre el menú full-screen.
//   • PosePickerSheet (mobile / "ver más" desktop) — bottom sheet con
//     tabs por categoría (Recomendados / Cuerpo completo / Plano medio /
//     Detalle) y grilla de poses tappable. Confirmación con "Listo (N)".
//
// El catálogo de poses se pasa como prop — fetch único arriba en
// InterfazV2 (es público, cambia poco).

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  POSE_CATEGORIES,
  type PoseV2,
} from './posesApi';
import { HoverScrollCarousel } from './HoverScrollCarousel';
import { useExitTransition } from '../../hooks/useExitTransition';

// ─── Inline (desktop) ─────────────────────────────────────────────────

interface PosePickerInlineProps {
  poses: PoseV2[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onOpenSheet: () => void;
  /** Si true, oculta el label "Pose" interno (lo provee el caller). */
  hideLabel?: boolean;
  /** Si true, fuerza selección exclusiva (radio-button). Lo usa el
   *  modo varios-modelos: con N personas en la pose no tiene sentido
   *  generar variaciones por pose. */
  singleSelect?: boolean;
}

export const PosePickerInline: React.FC<PosePickerInlineProps> = ({
  poses,
  selectedIds,
  onChange,
  onOpenSheet,
  hideLabel = false,
  singleSelect = false,
}) => {
  const toggle = (id: string) => {
    if (singleSelect) {
      // Tap sobre la ya seleccionada → deselecciona; sino reemplaza.
      onChange(selectedIds.includes(id) ? [] : [id]);
      return;
    }
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };
  return (
    <div className="flex flex-col gap-3">
      <div
        className={`flex items-center ${hideLabel ? 'justify-end' : 'justify-between'}`}
      >
        {!hideLabel && (
          <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
            Pose
          </p>
        )}
        <button
          type="button"
          onClick={onOpenSheet}
          className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
        >
          Ver más
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Carrusel — mismo aspect que las cards de modelo. Tap toggle.
          La `key` dispara `animate-mode-swap` cuando el catálogo
          filtrado cambia (ej. al cambiar la cantidad de modelos en
          multi mode → poses de N personas distintas). */}
      <HoverScrollCarousel
        key={`${poses.length}-${poses[0]?.id ?? ''}`}
        className="flex gap-3 overflow-x-auto -mx-5 px-5 scrollbar-hide animate-mode-swap"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        arrowInset={4}
      >
        {poses.slice(0, 12).map((p) => {
          const isSel = selectedIds.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              className={`shrink-0 w-[140px] aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
                isSel ? 'border-brand-primary' : 'border-neutral-200'
              } transition-all relative active:scale-[0.97]`}
            >
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt={p.nombre}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              )}
              {isSel && (
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </HoverScrollCarousel>
    </div>
  );
};

// ─── Sheet (mobile + "ver más" desktop) ───────────────────────────────

interface PosePickerSheetProps {
  open: boolean;
  poses: PoseV2[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
  /** Si true, fuerza selección exclusiva — mismo motivo que en el
   *  Inline: el modo varios-modelos no soporta multi-pose. */
  singleSelect?: boolean;
  /** Poses personalizadas del cliente (tab "Mis poses"). */
  customPoses?: PoseV2[];
  /** Sube una pose nueva (dispara el flujo n8n). */
  onUploadPose?: (file: File) => void;
  /** Borra una pose propia. */
  onDeletePose?: (pose: PoseV2) => void;
  /** true mientras se procesa una subida (spinner en el card "+"). */
  uploading?: boolean;
}

export const PosePickerSheet: React.FC<PosePickerSheetProps> = ({
  open,
  poses,
  selectedIds,
  onChange,
  onClose,
  singleSelect = false,
  customPoses = [],
  onUploadPose,
  onDeletePose,
  uploading = false,
}) => {
  const [tab, setTab] = useState<string>('recomendado');
  const fileRef = useRef<HTMLInputElement>(null);
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al seleccionar Listo /
  // Cancelar / overlay) — así el deslizamiento inverso se ve antes de
  // que el sheet desaparezca del DOM.
  const { mounted, animated } = useExitTransition(open, 300);
  // Working copy — solo commiteamos al cerrar con "Listo". Si el user
  // toca Cancelar / overlay, descartamos los cambios.
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);
  useEffect(() => {
    if (open) setDraftIds(selectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!mounted) return null;

  // Set de ids de "Mis poses" — para la exclusión mutua con las globales.
  const customIdSet = new Set(customPoses.map((p) => p.id));
  const toggle = (id: string) => {
    const isCustomId = customIdSet.has(id);
    setDraftIds((prev) => {
      if (singleSelect) {
        return prev.includes(id) ? [] : [id];
      }
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // No se mezclan "Mis poses" con globales: al agregar una, descartamos
      // las del otro grupo (así la default global preseleccionada se
      // deselecciona al elegir una custom, y viceversa).
      const sameGroup = prev.filter((x) => customIdSet.has(x) === isCustomId);
      return [...sameGroup, id];
    });
  };
  const handleConfirm = () => {
    onChange(draftIds);
    onClose();
  };

  // Filtra poses por categoría. Para 'recomendado' no tenemos un signal
  // de productModelViews en este nivel — mostramos todas como recomendadas
  // para no quedarnos sin contenido. El caller puede pasar un orden custom
  // a futuro si quiere priorizar algunas.
  const isMine = tab === 'mis_poses';
  const filtered = isMine
    ? customPoses
    : tab === 'recomendado'
      ? poses
      : poses.filter((p) => (p.categoria || '') === tab);

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile la altura del sheet (en className) ya excluye el
          // safe area top — bottom-anclado sin zona blanca arriba.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* ── Mobile: drag handle + iOS header (Cancelar / título / Listo) ── */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Elegí poses
          </h3>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={draftIds.length === 0}
            className="text-[15px] text-brand-primary font-semibold disabled:opacity-40 active:opacity-50 transition-opacity w-20 text-right"
          >
            {draftIds.length > 0 ? `Listo (${draftIds.length})` : 'Listo'}
          </button>
        </div>

        {/* ── Desktop: header título + X close, divider abajo (PruebaNueva). ── */}
        <div className="hidden md:flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            Pose del modelo
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hidden md:block mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Contenido — tabs + grilla. Padding y aspect cambian por
            breakpoint. Desktop usa px-5 + aspect 4/5 (PruebaNueva style),
            mobile sigue con px-4 + aspect 9/16. */}
        <div className="flex-1 overflow-y-auto px-4 md:px-5 pt-2 md:pt-4 pb-4">
          {/* Tabs */}
          <div
            className="flex flex-wrap gap-2 mb-3 md:mb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {[
              ...POSE_CATEGORIES,
              { id: 'mis_poses', label: 'Mis poses' },
            ].map((c) => {
              const isActive = tab === c.id;
              const isRecomendado = c.id === 'recomendado';
              const isMineTab = c.id === 'mis_poses';
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setTab(c.id)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 md:px-4 h-9 rounded-full border text-[13px] md:text-[15px] font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-brand-black/90 border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  {isRecomendado && <Sparkles className="w-3.5 h-3.5" />}
                  {isMineTab && <Plus className="w-3.5 h-3.5" />}
                  {c.label}
                </button>
              );
            })}
          </div>
          {!isMine && filtered.length === 0 ? (
            <p className="text-[13.5px] text-[#3C3C43]/55 mt-6 text-center">
              No hay poses en esta categoría.
            </p>
          ) : (
            // `key` por categoría + tamaño del catálogo: anima cuando
            // el user cambia de tab o cuando el filtro externo (la
            // cantidad esperada de personas en la pose) muta el set.
            <div
              key={`${tab}-${filtered.length}-${filtered[0]?.id ?? ''}`}
              className="grid grid-cols-3 gap-2.5 md:gap-3 animate-mode-swap"
            >
              {/* Card "+ Subir pose" — solo en "Mis poses" */}
              {isMine && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="relative w-full rounded-xl md:rounded-[13px] overflow-hidden border-2 border-dashed border-brand-primary/40 bg-brand-primary/[0.04] hover:bg-brand-primary/[0.08] disabled:opacity-60 aspect-[9/16] md:aspect-[4/5] flex flex-col items-center justify-center gap-2 text-brand-primary transition-colors active:scale-[0.97]"
                >
                  {uploading ? (
                    <span className="w-6 h-6 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-6 h-6" strokeWidth={2.5} />
                      <span className="text-[12px] font-semibold px-2 text-center leading-tight">
                        Subir pose
                      </span>
                    </>
                  )}
                </button>
              )}

              {filtered.map((p) => {
                const isSel = draftIds.includes(p.id);
                const card = (
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    className={`relative w-full rounded-xl md:rounded-[13px] overflow-hidden bg-white border-2 transition-all active:scale-[0.97] ${
                      isSel
                        ? 'border-brand-primary md:border-transparent md:ring-2 md:ring-brand-primary'
                        : 'border-neutral-200 md:border-transparent md:ring-1 md:ring-black/[0.06]'
                    }`}
                  >
                    {p.imageUrl && (
                      <img
                        src={p.imageUrl}
                        alt={p.nombre}
                        loading="lazy"
                        className={`block w-full aspect-[9/16] md:aspect-[4/5] bg-neutral-100 ${
                          p.isCustom
                            ? 'object-cover'
                            : 'object-cover md:object-[center_15%]'
                        }`}
                      />
                    )}
                    {isSel && (
                      <span className="absolute top-1.5 right-1.5 md:top-2.5 md:right-2.5 w-6 h-6 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      </span>
                    )}
                    {/* Nombre — solo en desktop y solo para poses globales
                        (las custom no tienen nombre, así que no mostramos
                        "Mi pose"). Mobile mantiene la card limpia. */}
                    {!p.isCustom && (
                      <p className="hidden md:block text-[13px] text-brand-black/90 px-3 py-2.5 font-medium text-center">
                        {p.nombre}
                      </p>
                    )}
                  </button>
                );
                if (!isMine) return <React.Fragment key={p.id}>{card}</React.Fragment>;
                // En "Mis poses": wrapper relativo con botón borrar (no se
                // pueden anidar <button>, por eso el 🗑️ es hermano del card).
                return (
                  <div key={p.id} className="relative group">
                    {card}
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            '¿Borrar esta pose? No se puede deshacer.',
                          )
                        ) {
                          onDeletePose?.(p);
                        }
                      }}
                      aria-label="Borrar pose"
                      className="absolute top-1.5 left-1.5 md:top-2.5 md:left-2.5 w-6 h-6 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-white text-red-500 hover:bg-red-50 transition md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input file oculto para subir poses (lo dispara el card "+"). */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadPose?.(f);
              e.currentTarget.value = '';
            }}
          />
        </div>

        {/* ── Desktop footer: Cancelar / Aceptar ── */}
        <div className="hidden md:flex px-5 py-3 justify-end gap-3 shrink-0 border-t border-black/[0.06]">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={draftIds.length === 0}
            className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
              draftIds.length > 0
                ? 'text-white bg-brand-primary hover:bg-brand-primary-hover'
                : 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
            }`}
          >
            {draftIds.length > 0 ? `Aceptar (${draftIds.length})` : 'Aceptar'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

// ─── Helper para mostrar el resumen de selección ──────────────────────

export const poseSelectionLabel = (ids: string[], poses: PoseV2[]): string => {
  if (ids.length === 0) return 'Sugerido';
  if (ids.length === 1) {
    const p = poses.find((x) => x.id === ids[0]);
    return p?.nombre || 'Pose';
  }
  return `${ids.length} poses`;
};

// Silenciador de imports no usados (mantengo símbolos por si se extiende)
void ChevronLeft;
void X;
